/**
 * QuickCreateTile — Tile individual do popover "Novo" (Quick Create)
 *
 * Espelha o layout do ShortcutTile do App Launcher (ícone grande centralizado
 * + rótulo curto), mas sem depender de AppShortcutDef. Suporta modo edição
 * (jiggle + botão remover) e favoritar via estrela.
 *
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 */
import { Star, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QuickCreateDef } from '@/config/quickCreate';

interface QuickCreateTileProps {
  action: QuickCreateDef;
  onActivate: (action: QuickCreateDef) => void;
  onToggleFavorite?: (key: string) => void;
  isFavorite?: boolean;
  editMode?: boolean;
  showFavoriteToggle?: boolean;
}

export function QuickCreateTile({
  action,
  onActivate,
  onToggleFavorite,
  isFavorite = false,
  editMode = false,
  showFavoriteToggle = true,
}: QuickCreateTileProps) {
  const Icon = action.icon;

  return (
    <div
      className={cn(
        'group relative flex h-24 flex-col items-center justify-center gap-1.5 rounded-xl p-2 text-center transition-all',
        'hover:bg-muted/70 focus-within:bg-muted/70 cursor-pointer select-none',
        editMode && 'animate-wiggle',
      )}
      onClick={() => !editMode && onActivate(action)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (editMode) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onActivate(action);
        }
      }}
      aria-label={action.label}
    >
      {editMode && onToggleFavorite && (
        <button
          type="button"
          className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow ring-1 ring-background hover:bg-destructive/90"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(action.key);
          }}
          aria-label={`Remover ${action.label} dos favoritos`}
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {!editMode && showFavoriteToggle && onToggleFavorite && (
        <button
          type="button"
          className={cn(
            'absolute right-1 top-1 rounded-md p-1 opacity-0 transition-opacity hover:bg-background/70 group-hover:opacity-100',
            isFavorite && 'opacity-100',
          )}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(action.key);
          }}
          aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <Star
            className={cn(
              'h-3.5 w-3.5',
              isFavorite ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground',
            )}
          />
        </button>
      )}

      <div
        className={cn(
          'flex h-11 w-11 items-center justify-center rounded-xl bg-muted/60 transition-colors group-hover:bg-background',
          action.accent ?? 'text-primary',
        )}
      >
        <Icon className="h-6 w-6" strokeWidth={1.8} />
      </div>
      <span className="line-clamp-2 text-[11px] font-medium leading-tight text-foreground">
        {action.label}
      </span>
    </div>
  );
}
